import { useMemo } from 'react';
import { DeductibleType, useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT, RAW_DATE_FORMAT } from '@/common/constants';
import { DATE_FORMAT_VALIDATOR } from '@/common/validators';
import { API_URL } from '@/dtos';
import { TriggerCategoryType } from '@/modules/HealthPlan/HealthPlan.types.ts';
export const QUERY_KEY = 'GET_DEDUCTIVLES';
export const NO_DEADLINE_LABEL = 'No deadline';
export const NOT_AVAILABLE_LABEL = 'No available tier';





export default (
  employeeId?: string,
  serviceOn?: string,
  selectedHealthPlanId?: string,
  dependentId?: string,
  isIndividual?: boolean
) => {
  const isServiceOnValidForCall = useMemo(() => {
    if (!serviceOn) return false;
    return DATE_FORMAT_VALIDATOR(serviceOn);
  }, [serviceOn]);

  const { isLoading, data } = useQuery(
    [QUERY_KEY, employeeId, serviceOn, dependentId, isIndividual],
    () =>
      api.get(API_URL.TIERS_DEDUCTIBLES, {
        params: {
          employee_id: employeeId,
          deductible_type: isIndividual ? DeductibleType.Individual : DeductibleType.Family,
          ...(selectedHealthPlanId && { health_plan_id: selectedHealthPlanId }),
          ...(serviceOn &&
            DATE_FORMAT_VALIDATOR(serviceOn) && { service_on: dayjs(serviceOn).format(RAW_DATE_FORMAT) }),
          ...(dependentId && employeeId !== dependentId && { dependent_id: dependentId }),
        },
      }),
    {
      cacheTime: 0,
      enabled: !!employeeId && isServiceOnValidForCall,
    }
  );

  const healthPlanId = useMemo(() => _.toString(data?.data?.health_plan_id), [data]);
  const healthPolicyId = useMemo(() => _.toString(data?.data?.health_policy_id), [data]);
  const healthPlanName = useMemo(() => data?.data?.health_plan_name, [data]);
  const formattedDeductiblesData = useMemo(() => {
    const list = data?.data?.deductibles || [];
    return list.map((deductible) => ({
      accumulated: deductible.accumulated || 0,
      type: deductible.deductible_type as unknown as DeductibleType,
      employeeId: deductible.employee_id ? `${deductible.employee_id}` : null,
      dependentId: deductible.dependent_id ? `${deductible.dependent_id}` : null,
    }));
  }, [data]);

  const formattedTiersData = useMemo(() => {
    const list = data?.data?.tiers || [];
    return list.map((item) => ({
      id: `${item.id}`,
      name: item.name || '',
      validFrom: item?.valid_from ? dayjs(item.valid_from).format(DEFAULT_DATE_FORMAT) : undefined,
      validTo: item.valid_to
        ? dayjs(item.valid_to).format(DEFAULT_DATE_FORMAT)
        : item.valid_from
          ? NO_DEADLINE_LABEL
          : NOT_AVAILABLE_LABEL,
      triggers: (item?.triggers || []).map((trigger) => ({
        type: trigger.type,
        triggerCategoryType: trigger.trigger_category_type as unknown as TriggerCategoryType,
        name: trigger.name,
        threshold: trigger.threshold_amount,
      })),
    }));
  }, [data]);

  const formattedFamilyDeductibles = useMemo(() => {
    const currentData = data?.data;
    if (!currentData) return undefined;

    return {
      familyRemainingToNextTier: currentData?.family_remaining_to_next_tier,
      familyCurrentTier: currentData?.family_current_tier,
      healthPlanId: currentData?.health_plan_id,
      healthPlanName: currentData?.health_plan_name,
      coverageLevel: currentData?.coverage_level,
    };
  }, [data]);

  return {
    isLoading,
    healthPlanId,
    healthPolicyId,
    healthPlanName,
    formattedDeductiblesData,
    formattedTiersData,
    formattedFamilyDeductibles,
  };
};
